import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Megaphone, Pin, ArrowLeft } from "lucide-react";
import { useApi } from "@/hooks/useApi";
import { PageHeader, LoadingGrid, ErrorState, EmptyState, Pager, Media } from "@/components/common";
import { useBranding } from "@/context/BrandingContext";
import { ftime, label } from "@/lib/format";

export function PostCard({ post, full, actions }) {
  const { branding } = useBranding();
  return (
    <article className="uc-card p-5 sm:p-6" data-testid={`channel-post-${post.id}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full gold-bg grid place-items-center"><Megaphone className="h-4 w-4 text-black" /></div>
          <div>
            <p className="text-sm font-medium">{branding.website_name}</p>
            <p className="text-[11px] text-muted-foreground">{ftime(post.created_at)} · {label(post.post_type)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {post.pinned && <span className="text-primary flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider" data-testid="post-pinned-badge"><Pin className="h-3.5 w-3.5" />Pinned</span>}
          {actions}
        </div>
      </div>
      {post.title && <h3 className="font-display text-xl mb-2">{post.title}</h3>}
      {post.body && <p className={`text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed ${full ? "" : "line-clamp-4"}`}>{post.body}</p>}
      {post.media?.length > 0 && (
        <div className={`grid gap-2 mt-4 ${post.media.length > 1 ? "grid-cols-2" : ""}`}>
          {(full ? post.media : post.media.slice(0, 2)).map((m, i) => <Media key={i} item={m} className="max-h-[420px]" />)}
        </div>
      )}
      {!full && <Link to={`/channel/${post.id}`} className="inline-block mt-4 text-xs text-primary hover:underline" data-testid={`open-post-${post.id}`}>Open post</Link>}
    </article>
  );
}

export function ChannelFeed() {
  const { branding } = useBranding();
  const [page, setPage] = useState(1);
  const { data, loading, error, reload } = useApi("/channel", { page });
  return (
    <div className="max-w-2xl">
      <PageHeader eyebrow="Official channel" title={`${branding.website_name} Channel`} desc="Announcements and updates from the team." />
      {loading ? <LoadingGrid count={3} className="space-y-4" h="h-40" /> : error ? <ErrorState message={error} onRetry={reload} /> :
        data.items.length === 0 ? <EmptyState icon={Megaphone} title="No announcements yet." testid="channel-empty" /> : (
          <div className="space-y-4 stagger">{data.items.map((p) => <PostCard key={p.id} post={p} />)}</div>
        )}
      {data && <Pager page={page} total={data.total} limit={10} onPage={setPage} />}
    </div>
  );
}

export function ChannelPostPage() {
  const { id } = useParams();
  const { data, loading, error, reload } = useApi(`/channel/${id}`);
  return (
    <div className="max-w-2xl">
      <Link to="/channel" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-6" data-testid="post-back-link"><ArrowLeft className="h-4 w-4" />Channel</Link>
      {loading ? <LoadingGrid count={1} className="" h="h-60" /> : error ? <ErrorState message={error} onRetry={reload} /> : <PostCard post={data} full />}
    </div>
  );
}
